import React, { useEffect, useMemo, useState } from 'react';
import { productEffectivePrice } from '../productPricing';
import {
  formatCharCustomizationLabel,
  isAllowedCharLetter,
  normalizePersonalizationLetter,
  productCharPersonalizationEnabled,
} from '../productPersonalization';
import { useCart } from './CartContext';
import { isOutOfStock, lineMaxQty, normalizeStockQty } from './cartUtils';
import QtyStepper from '../checkout/QtyStepper';
import { formatINR } from '../checkout/formatINR';
import CharacterPicker from './CharacterPicker';
import MonogramLetterSelect from './MonogramLetterSelect';

const CART_ICON = (
  <svg className="add-to-cart-btn__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
    <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
  </svg>
);

export default function AddToCartButton({
  product,
  categorySlug,
  className = '',
  compact = false,
  variant = 'default',
}) {
  const isInline = variant === 'inline';
  const isFeatured = variant === 'featured';
  const isCompact = compact || isInline;
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [letter, setLetter] = useState('');
  const [error, setError] = useState('');
  const [justAdded, setJustAdded] = useState(false);
  const price = productEffectivePrice(product);
  const stockQty = useMemo(() => normalizeStockQty(product?.stock_qty), [product?.stock_qty]);
  const stockMax = useMemo(() => lineMaxQty(stockQty), [stockQty]);
  const outOfStock = isOutOfStock(stockQty);
  const charEnabled = productCharPersonalizationEnabled(product);

  useEffect(() => {
    if (outOfStock) return;
    setQty((prev) => Math.max(1, Math.min(stockMax, prev)));
  }, [stockMax, outOfStock]);

  if (!product) return null;

  if (price <= 0) {
    return null;
  }

  if (outOfStock) {
    return (
      <div className={`product-purchase-panel add-to-cart-block add-to-cart-block--oos ${className}`.trim()}>
        <button type="button" className="add-to-cart-block__btn" disabled>
          Out of stock
        </button>
      </div>
    );
  }

  const onAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    if (charEnabled) {
      const c = normalizePersonalizationLetter(letter);
      if (!c || !isAllowedCharLetter(c, product)) {
        setError('Choose a letter before adding to bag.');
        return;
      }
      const res = addItem(product, categorySlug, qty, c);
      if (!res.ok) {
        setError(res.error || 'Could not add.');
        return;
      }
    } else {
      const res = addItem(product, categorySlug, qty);
      if (!res.ok) {
        setError(res.error || 'Could not add.');
        return;
      }
    }
    setJustAdded(true);
    window.setTimeout(() => setJustAdded(false), 1400);
  };

  const lineTotal = price * qty;
  const selectedLetter = normalizePersonalizationLetter(letter);

  const blockClass = [
    isInline || isCompact ? '' : 'product-purchase-panel',
    'add-to-cart-block',
    isCompact ? 'add-to-cart-block--compact' : '',
    isInline ? 'add-to-cart-block--inline' : '',
    isFeatured ? 'add-to-cart-block--featured' : '',
    charEnabled ? 'add-to-cart-block--char' : '',
    charEnabled && isCompact ? 'add-to-cart-block--monogram-card' : '',
    charEnabled && isCompact && selectedLetter ? 'add-to-cart-block--letter-ready' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  const stepperSize = isInline ? 'xs' : 'sm';
  const bagLabel = justAdded
    ? 'Added ✓'
    : charEnabled && selectedLetter
      ? isCompact
        ? `Add · ${selectedLetter}`
        : `Add letter ${selectedLetter}`
      : charEnabled && isCompact
        ? 'Add to bag'
        : isInline
          ? 'Add'
          : 'Add to bag';

  return (
    <div className={blockClass} onClick={(e) => e.stopPropagation()}>
      {charEnabled && isCompact && !isInline ? (
        <MonogramLetterSelect
          product={product}
          value={letter}
          onChange={setLetter}
          disabled={justAdded}
          id={`cart-char-${product?.id || 'p'}`}
        />
      ) : charEnabled ? (
        <CharacterPicker
          product={product}
          value={letter}
          onChange={setLetter}
          disabled={justAdded}
          idPrefix={`cart-char-${product?.id || 'p'}`}
          compact={false}
        />
      ) : null}
      <div className="add-to-cart-block__row">
        {!isFeatured ? (
          <div className="add-to-cart-block__qty">
            <span className={`add-to-cart-block__label${isInline ? ' visually-hidden' : ''}`}>Qty</span>
            <QtyStepper
              size={stepperSize}
              value={qty}
              min={1}
              max={stockMax}
              onChange={(n) => setQty(Math.max(1, Math.min(stockMax, n || 1)))}
              label={`Quantity for ${product?.name || 'product'}`}
            />
          </div>
        ) : null}
        <button
          type="button"
          className={`add-to-cart-block__btn${isInline ? ' add-to-cart-block__btn--slim' : ''}${
            justAdded ? ' add-to-cart-block__btn--success' : ''
          }`}
          onClick={onAdd}
          disabled={justAdded || (charEnabled && !selectedLetter)}
          aria-label={
            isInline ? (justAdded ? 'Added to bag' : `Add ${product?.name || 'product'} to bag`) : undefined
          }
        >
          {CART_ICON}
          <span>{bagLabel}</span>
        </button>
      </div>
      {!isCompact ? (
        <p className="add-to-cart-block__hint small text-muted mb-0">
          {formatINR(price)} each · {formatINR(lineTotal)} for {qty}
          {charEnabled && selectedLetter
            ? ` · ${formatCharCustomizationLabel(selectedLetter)}`
            : ''}
          {stockMax < 99 ? ` · ${stockMax} in stock` : ''}
        </p>
      ) : null}
      {error ? (
        <span className="add-to-cart-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
